import { useEffect } from "react";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import isBreakpointMobile from "domains/commons/isBreakpointMobile";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { extraTheme } from "domains/theme";
import Button from "domains/ui/components/Button";
import Divider from "domains/ui/components/Divider";
import Icon from "domains/ui/components/Icon";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";

import {
  Box,
  Flex,
  HStack,
  IconButton,
  Image,
  useBreakpoint,
  useDisclosure,
} from "@chakra-ui/react";

import BackgroundTasksDropdown from "./BackgroundTasksDropdown";
import NavBar from "./NavBar";
import NavDrawer from "./NavDrawer";
import NewsIndicator from "./NewsIndicator";
import OrganizationDropdown from "./OrganizationDropdown";
import ProfileDropdown from "./ProfileDropdown";
import ProjectDropdown from "./ProjectDropdown";
import SearchButton from "./SearchButton";
import SubscriptionWarning from "./SubscriptionWarning";

export const TOP_BAR_HEIGHT = 56;

export default function TopBar() {
  const router = useRouter();
  const { hasNoProject } = useTeamContext();
  const { isUnsigned } = useUserContext();
  const {
    isOpen: isNavDrawerOpen,
    onToggle: onNavDrawerToggle,
    onClose: onNavDrawerClose,
  } = useDisclosure();
  const breakpoint = useBreakpoint({ ssr: true });
  const isMobile = isBreakpointMobile(breakpoint) || breakpoint === "md";
  const isCompact = breakpoint === "lg";
  const [isCollectionEverShown, setIsCollectionEverShown] = usePersistedState(
    PersistedStateKey.COLLECTIONS_SHOWN,
    { defaultValue: false }
  );

  useEffect(() => {
    if (isCollectionEverShown || !router.isReady) return;
    if (router.pathname === "/collections") {
      setIsCollectionEverShown(true);
    }
  }, [router, isCollectionEverShown, setIsCollectionEverShown]);

  return (
    <>
      <Flex
        pos="fixed"
        zIndex="banner"
        align="center"
        justify="space-between"
        gap={4}
        w="100%"
        h={`${TOP_BAR_HEIGHT}px`}
        px={isMobile ? 2 : 4}
        borderBottomWidth="1px"
        borderBottomColor="border.500"
        transition={extraTheme.transitions.fast}
        bgColor="black.500"
      >
        {/* Left */}
        <HStack pos="relative" zIndex={1} flex={0} whiteSpace="nowrap">
          {isMobile ? (
            <IconButton
              aria-label="Open menu"
              onClick={onNavDrawerToggle}
              size="md"
              variant="unstyled"
            >
              <Icon color="textSecondary" id="Ui/Drawer" h="16px" />
            </IconButton>
          ) : (
            <Button
              w="36px"
              h="36px"
              p={0}
              ml={-1}
              mr={2}
              borderRadius="md"
              variant="filterGhost"
              internalLink="/"
              onClick={() => {
                Track(AnalyticsEvents.Navigation.ClickedHomeLogo, {
                  userIsAuthenticated: !isUnsigned,
                });
              }}
            >
              <Image
                w="24px"
                minW="24px"
                alt="logo"
                src="/logos/scenario.svg"
              />
            </Button>
          )}

          {!isUnsigned && <OrganizationDropdown />}
          {!isUnsigned && <ProjectDropdown isCompact={isMobile} />}
        </HStack>

        {/* Center */}
        {!isMobile && (
          <Box
            {...((!isCompact || isUnsigned) && {
              pos: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            })}
          >
            <NavBar isCompact={isCompact} />
          </Box>
        )}

        {/* Right side */}
        <HStack
          pos="relative"
          zIndex={1}
          align="center"
          flex={0}
          whiteSpace="nowrap"
          spacing={2}
        >
          {!isUnsigned && !hasNoProject && (
            <>
              <SubscriptionWarning />
              <SearchButton isCompact={isMobile || isCompact} />
              {!isMobile && (
                <Divider mx={1} dividerProps={{ w: "1px", h: "14px" }} />
              )}
            </>
          )}

          {!isUnsigned && (
            <>
              <BackgroundTasksDropdown />

              {!isMobile && (
                <>
                  <Button
                    w="36px"
                    h="36px"
                    p={0}
                    borderRadius="md"
                    internalLink="/collections"
                    tooltip="Collections"
                    variant="alpha"
                    onClick={() => {
                      Track(AnalyticsEvents.Navigation.ClickedCollections, {
                        context: "top-bar",
                        userIsAuthenticated: !isUnsigned,
                      });
                    }}
                  >
                    {!isCollectionEverShown && <NewsIndicator />}
                    <Icon id="Nav/Collection/Outline" h="14px" />
                  </Button>

                  <Divider mx={1} dividerProps={{ w: "1px", h: "14px" }} />
                </>
              )}

              <ProfileDropdown />
            </>
          )}

          {isUnsigned && (
            <>
              <Button size="sm" variant="secondary" internalLink="/login">
                Log In
              </Button>
              <Button size="sm" variant="primary" internalLink="/login">
                Sign Up
              </Button>
            </>
          )}
        </HStack>
      </Flex>

      {isMobile && (
        <NavDrawer isOpen={isNavDrawerOpen} onClose={onNavDrawerClose} />
      )}
    </>
  );
}
